/* eslint-disable no-undef */
// noinspection OverlyComplexFunctionJS
import helperServiceAuth from './helperServiceAuth'
import helperServiceTextNumber from './helperServiceTextNumber'
import helperServiceVars from './helperServiceVars'
import ReactGA from 'react-ga4'
import mongoose from 'mongoose'
import {format} from 'date-fns'
import tg from './tailwindGroups'
import React, {useState} from 'react'


/**
 * Miscellaneous Services
 * @author Jared
 */

/* eslint-disable max-lines */
// noinspection OverlyNestedFunctionJS
const helperService = {
	fetch: async (path, options, callback, error) => {
		let host = window?.location?.origin

		if (!host.includes('localhost')) host = host.replace('http://', 'https://')
		fetch(host + path, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(options)
		})
			.then((response) => {
				if (!response.ok) {
					if (error) error()
					return
				}
				return response?.json()
			})
			.then(result => {
				if (callback)
					callback(result)
			})
	},
	/**
	 * @author Jared
	 * @description Filter outliers from an array of numbers
	 *
	 * @param {Array} inputArray - The array to filter
	 * @param {String} [method] - One of the following: 'quartile', 'jump', 'none'
	 *
	 * @return {Array} - returns the updated filtered array
	 */
	filterOutliers: (inputArray, method) => {
		if (!inputArray || inputArray.length < 4)
			return inputArray

		if (!method || method === 'quartile') {

			let values, q1, q3, iqr, maxValue, minValue

			values = inputArray.slice().sort((a, b) => a - b) //copy array fast and sort

			if ((values.length / 4) % 1 === 0) {//find quartiles
				q1 = 1 / 2 * (values[(values.length / 4)] + values[(values.length / 4) + 1])
				q3 = 1 / 2 * (values[(values.length * (3 / 4))] + values[(values.length * (3 / 4)) + 1])
			} else {
				q1 = values[Math.floor(values.length / 4 + 1)]
				q3 = values[Math.ceil(values.length * (3 / 4) + 1)]
			}

			iqr = q3 - q1
			maxValue = q3 + iqr * 1.5
			minValue = q1 - iqr * 1.5
			return values.filter((x) => (x >= minValue) && (x <= maxValue))
		} else if (method === 'jump') {
			let sorted = inputArray.sort((a, b) => b - a)
			const distances = [],
				updated = []

			sorted.forEach((num, index) => {
				if (index > 0) {
					distances.push(sorted[index - 1] - num)
				}
			})
			distances.sort((a, b) => b - a)
			const jumpLimit = distances.slice(0, Math.ceil(inputArray.length * .005)).slice(-1)[0]

			let jumped = false
			sorted.forEach((num, index) => {
				if (index > 0 && num > 0) {
					if ((sorted[index - 1] - num) < jumpLimit || jumped === true)
						updated.push(sorted[index - 1])
					if ((sorted[index - 1] - num) < jumpLimit)
						jumped = true

				}
			})
			return updated
		} else if (Array.isArray(method)) {
			return inputArray.filter(post => method.indexOf(post) === -1)
		} else if (method === 'none')
			return inputArray

	},
	/**
	 * @author Jared
	 * @description The function calls post API and return appropriate data
	 *
	 * @param {String} path - Accept api path as string
	 * @param {Object} options - Accept options or payload as object
	 *
	 * @return {Object} - returns a promise with the appropriate data
	 */
	awaitFetch: async (path, options) => new Promise((resolve, reject) => {
		try {
			// let host = window?.location?.origin
			// if (!host.includes('localhost')) host = host.replace('http://', 'https://')

			const host = process.env.NEXT_PUBLIC_URL

			fetch(host + path, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(options)
			})
				.then((response) => {
					if (!response.ok)
						return
					try {
						return response?.json()
					} catch (error) {
						console.log('error', error)
						reject()
					}
				})
				.then(result => {
					resolve(result)
				})
		} catch (error) {
			console.log('error at hs.awaitFetch - ', error)
			reject()
		}
	}),
	/**
	 * @author Jared
	 * @description The function calls post API with file headers and return appropriate data
	 *
	 * @param {String} path - Accept api path as string
	 * @param {Object} options - Accept options or payload as object
	 *
	 * @return {Object} - returns a promise with the appropriate data
	 */
	awaitFetchFile: async (path, options) => new Promise((resolve, reject) => {
		try {
			let formData = new FormData()

			Object.keys(options).forEach(key => {
				if (key === 'file')
					formData.append(key, options[key], options[key].name)
				else
					formData.append(key, options[key])
			})

			let host = window?.location.origin
			if (!host.includes('localhost')) host = host.replace('http://', 'https://')

			fetch(host + path, {
				method: 'POST',
				body: formData
			})
				.then((response) => {
					if (!response.ok)
						return
					try {
						return response?.json()
					} catch (error) {
						console.log('error', error)
						reject()
					}
				})
				.then(result => {
					resolve(result)
				})
		} catch (error) {
			console.log('await fetch error', error)
			reject()
		}
	}),
	/**
	 * @author Jared
	 * @description The function returns a post's clickable url
	 *
	 * @param {String} post - Accept string
	 *
	 * @return {String} - returns a post's clickable url
	 */
	getPostURL: post => {
		if (post.platform === 'instagram')
			return `https://www.instagram.com/p/${post.platformId}`
		if (post.platform === 'twitter')
			return `https://twitter.com/${post.username}/status/${post.platformId}`
		return ''
	},
	/**
	 * @author Jared
	 * @description Creates new clone object
	 *
	 * @param {Object} obj - Accept object
	 *
	 * @return {Object} returns cloned object - when structuredClone doesn't do it
	 */
	clone: obj => {
		if (obj) return JSON.parse(JSON.stringify(obj))
	},
	/**
	 * @author Jared
	 * @description Creates a log entry
	 *
	 * @param {string} type - type of log from list helperService.vars.logTypes
	 * @param {string} description - description of the log
	 * @param {string} [user] - id of the user
	 * @param {string} [userName] - name of the user
	 * @param {string} [team] - id of the user
	 * @param {string} [teamName] - name of the team
	 * @param {Boolean} [server] - server, ignore backend calls
	 *
	 * @return {Boolean} - success or failure
	 */
	log: async (type, description, user, userName, team, teamName, server) =>
		// eslint-disable-next-line no-undef
		new Promise(async (resolve, reject) => {
			const data =  {
				type,
				description,
				user,
				userName: userName || 'Unknown User',
				team,
				teamName: teamName || 'Unknown Team',
				page: typeof window !== 'undefined' ? window?.location?.pathname : '',
				date: new Date()
			}
			if (!server) {
				data.browser = helperService.checkBrowser()

				await helperService.awaitFetch('/api/runner', {
					action: 'manage',
					type: 'Log',
					object: data
				})
					.catch(() => reject())
					.then(() => resolve())
			}
		}),
	basicLog: async (description, page, vars) =>
		new Promise(async (resolve, reject) => {
			const data =  {
				description,
				page,
				user: vars?._id || 'Unknown User ID',
				userName: vars?.email || 'Unknown User Email',
				date: new Date(),
				browser: helperService.checkBrowser()
			}
			if (process.env.NODE_ENV === 'development')
				console.log('%cLog Mirror', 'color: #FFCE1A', `${description} on page ${page}`)

			await helperService.awaitFetch('/api/runner', {
				action: 'manage',
				type: 'Log',
				object: data
			})
				.catch((reason) => {
					console.log('log error: ', reason)
					reject()
				})
				.then(() => {
					resolve()
				})
		}),
	/**
	 * @author Stephen Rains
	 * @description Creates a log entry & adds to GA
	 *
	 * @param {object} type - type of log from list helperService.vars.logTypes
	 * @param {string} desc - description of the log
	 * @param {object} [user] - full user vars (to grab team, _id, name, etc.)
	 * @param {string} [event] - GA event
	 * @param {object} [gaPayload] - payload for GA
	 *
	 * Payload Data: {string} [page] - name of page user is on
	 * {string} [action] - description of action the user attempted to perform
	 * {string} [interaction] - way user interacted w/ item (click, type, etc.)
	 * {string} [interactionType] - what user was interacting with (button, text field, image, tool, etc.)
	 *
	 * @return {Boolean} - success or failure
	 */
	logPlusGA: async (type, desc, user, event, gaPayload) => {
		console.log('logPlusGA Activated')
		new Promise(async (resolve, reject) => {
			await helperService.awaitFetch('/api/runner', {
				action: 'manage',
				type: 'Log',
				object: {
					type,
					description: desc,
					user: user?._id || 'Unknown User',
					userName: user?.firstName + ' ' + user?.lastName || 'Unknown User',
					team: user?.team || 'Unknown team',
					teamName: 'Unknown Team',
					page: typeof window !== 'undefined' ? window?.location?.pathname : '',
					browser: helperService.checkBrowser(),
					date: new Date()
				}
			})
				.catch(() => reject())
				.then(() => resolve())
		})

		ReactGA.gtag('event', event, gaPayload)
	},
	/**
	 * @author Jared
	 * @description  The Function checks which browser is being using
	 *
	 * @return {String} - returns browser name
	 */
	checkBrowser: () => {
		if (typeof window === 'undefined')
			return 'chrome'
		if (typeof InstallTrigger !== 'undefined')
			return 'firefox'
		else if (
			/constructor/i.test(window?.HTMLElement) ||
			(function (p) {
				return p.toString() === '[object SafariRemoteNotification]'
				// eslint-disable-next-line no-undef
			})(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification))
		)
			return 'safari'
		else if (/*@cc_on!@*/!!document.documentMode)
			return 'ie'
		else if (!!window?.StyleMedia)
			return 'edge'
		else if (!!window?.chrome)
			return 'chrome'
		else return 'unknown'
	},
	update: async (type, _id, data, callback) => {
		await helperService.fetch('/api/runner', {
			action: 'update',
			type,
			_id,
			data
		}, result => {
			if (callback)
				callback(result)
		})
	},
	/**
	 * @author Jared
	 *
	 * @param {String} content
	 * @param {String} recipients
	 * @param {String} subject
	 * @return {Promise<void>}
	 */
	mail: async (content, recipients, subject) => {
		return await helperService.awaitFetch('/api/mail', {
			action: 'send',
			content: content,
			recipients: recipients,
			subject: subject,
		})
	},
	/**
	 * @author Stephen Rains
	 *
	 * @param {String} content
	 * @param {String} recipients
	 * @param {String} subject
	 * @return {Promise<Response>}
	 * Only differs because API does not contain window and fails so must use fetch instead of helperService
	 */
	apiMail: async (content, recipients, subject) => {
		return await fetch('https://dev.letaggiedoit.com/api/mail', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				action: 'send',
				content: content,
				recipients: recipients,
				subject: subject,
			}),
		})
	},
	/**
	 * @author Jared
	 * @description This function works like loader
	 *
	 * @output make page pause
	 *
	 */
	setThinking: () => {
		if (document?.getElementById('main')) {
			const mainEl = document.getElementById('main')
			mainEl.style.borderColor = helperServiceVars.vars.hexColors.blue
			mainEl.style.borderBottomWidth = '.5rem'
			mainEl.style.transition = 'border-bottom 0.3s ease'
		}
	},
	/**
	 * @author Jared
	 * @description  This function remove loading functionality
	 *
	 * @output make page resume
	 *
	 */
	unsetThinking: () => {
		if (document?.getElementById('main')) {
			document.getElementById('main').style.borderColor = helperServiceVars.vars.hexColors.green
			document.getElementById('main').style.borderBottomWidth = '.1rem'
		}
	},
	/**
	 * @author Jared
	 * @description  The Function update user profile based on give new detail
	 *
	 * @param {Object} user - Accept users detail object
	 * @param {String} field - Field Name
	 * @param {Any} value - Accept value that can be any type as per field requirement
	 *
	 * @output Update user profile
	 *
	 */
	updateProfile: async (user, field, value) => {
		helperService.setThinking()
		const data = {}
		data[field] = value
		await helperService.awaitFetch('/api/runner', {
			action: 'update',
			type: 'User',
			_id: user._id,
			data
		})
	},
	updateHistory: async (user, page) => {
		if (user && user !== 'logged out') {
			user.history = user.history || {}
			if (page) {
				user.history.pages = user.history.pages || []
				const pageIndex = user.history.pages.findIndex(innerPage => innerPage.url === page.url)

				if (pageIndex === -1)
					user.history.pages.push({
						url: page.url,
						accessed: new Date().getTime(),
						title: page.title,
						image: page.image
					})
				else {
					user.history.pages[pageIndex].accessed = new Date().getTime()
					user.history.pages[pageIndex].title = page.title
				}

				user.history.pages = user.history.pages.sort((b, a) => a.accessed - b.accessed).slice(0, 100)
				helperService.updateProfile(user, 'history', user.history)
				return user.history
			}
		}
	},
	/**
	 * @author Jared
	 * @description This function will give a color code that is a typical shade of one color
	 *
	 * @param {Number} percentage - Accept number as percentage
	 *
	 * @return  {String} - Color code
	 */
	getColorForPercentage: percentage => {
		const colors = helperServiceVars.vars.colors
		if (percentage >= 90) return colors[0]
		if (percentage >= 80) return colors[1]
		if (percentage >= 70) return colors[2]
		if (percentage >= 60) return colors[3]
		if (percentage >= 50) return colors[4]
		if (percentage >= 40) return colors[5]
		if (percentage >= 30) return colors[6]
		if (percentage >= 20) return colors[7]
		if (percentage >= 10) return colors[8]
		return colors[9]
	},
	/**
	 * @author Jared
	 * @description This function will give a color code as per given category
	 *
	 * @param {String} category - Accept category name as string
	 *
	 * @return {String} - Color code
	 */
	getColorForCategory: category => {
		const colors = helperServiceVars.vars.richColors
		if (!category) return colors[0]
		switch (category.toLowerCase()) {
		case 'music':
			return colors[0]
		case 'sports':
			return colors[1]
		case 'entertainment':
			return colors[2]
		case 'body mind & soul':
			return colors[3]
		case 'consumer goods':
			return colors[4]
		case 'culture':
			return colors[5]
		case 'industry & education':
			return colors[6]
		case 'sociopolitical':
			return colors[7]
		case 'reading':
			return colors[8]
		case 'not found':
			return colors[9]
		case 'misc.':
			return colors[9]
		default:
			return colors[0]
		}
	},
	/**
	 * @author Jared
	 * @description chatGPT or other AI passthrough
	 *
	 * @param {Object} vars - For the user and team
	 * @param {String|Array} query - The query to send to the AI
	 * @param {Boolean} [force] - Bypass the cache SET TO TRUE TO GET NEW STUFF
	 * @param {Boolean} [image] - Is this an image query?
	 * @param {Boolean} [checkBeforeCache] - Check the validity response before caching
	 * @param {Number} [count] - Number of images to generate
	 * @param {String} [textModel] - The text model to use
	 * @param {String} [imageModel] - The image model to use
	 * @param {String} [image_url] - The image url to use if analyzing image
	 * @param {Function} [alert] - The alert function to use
   * @param {Number} [tries] - Number of tries to get a valid response
	 *
	 * @return {Promise} - Chat Response
	 */
	askGPT: async function askGPT(vars, query, force, image, checkBeforeCache, count, textModel, imageModel, image_url, alert, tries) {
		if (tries > 3) {
			// helperService.basicLog(`We have tried the AI run ${tries} times - aborting.`, 'Planner', vars.user)
			alert('Sorry, I was unable to get a response from the AI. Please try again later.', 'error')
			return
		}

		const user = vars.user._id.toString(),
			team = vars.user.team._id.toString()

		const options = {action: 'fetchOne', type: 'ChatCache', query: {query}},
			showReport = true

		if (image) {
			options.query.image = true
			options.query.created = {$gt: new Date(new Date().getTime() - 1000 * 60 * 60)}
		}

		if (showReport)
			console.log('%cGPT Report', 'color: #F00', query, {
				'Force Fresh Results': force,
				'Is Image': image,
				'Number Requested': count,
				'Text Model': textModel || 'gpt-4.1-2025-04-14',
				'Image Model': imageModel || 'gpt-image-1',
				'Image if Referenced': image_url
			})

		let cache = false
		if (!force)
			cache = await helperService.awaitFetch('/api/runner', options)
		if (!cache) {
			let result = await helperService.awaitFetch('/api/aiRunner', {
				user,
				team, // currently unused, fyi
				query,
				force: force || false,
				image: image || false,
				checkBeforeCache,
				count: count || 1,
				textModel: (vars.user.preferences.aimodel === 'DeepSeek' && !image_url && !image) ? 'DeepSeek' : textModel,
				imageModel,
				image_url
			})
			if (image) {
				await helperService.awaitFetch('/api/runner',
					{
						action: 'manage',
						type: 'ChatCache',
						object: {query, image: true, result: result, results: result}
					})
			} else {
				if (checkBeforeCache) {
					await helperService.awaitFetch('/api/runner',
						{
							action: 'manage',
							type: 'ChatCache',
							object: {query, result: result, results: result}
						})
				} else {
					await helperService.awaitFetch('/api/runner',
						{
							action: 'manage',
							type: 'ChatCache',
							object: {query, result: result, results: result}
						})
				}
			}
			// console.log('%cGPT Report', 'color: #FF61D3', query, result, {'Force Fresh Results': force,
			// 	'Is Image': image, 'Number Requested': count, 'Text Model': textModel || 'gpt-4o-mini', 'Image Model': imageModel || 'dall-e-3', 'Image if Referenced': image_url})
			if (!result || (Array.isArray(result) && result.length < 1) ||
				(Array.isArray(result) && result.some(element => element.includes("I'm sorry")))) {
				// helperService.basicLog(`AI failed on initial request for user - trying again. User NOT notified. ${image ? 'Reverted to old image generator.' : ''}`, 'Planner', vars.user)
				result = await askGPT(vars, query, force, image, checkBeforeCache, count, textModel, imageModel, image_url, alert, (tries || 1) + 1)
			}
			return result
		} else {
			if (showReport)
				console.log('%cGPT Report CACHED', 'color: #FF61D3', query, Array.isArray(query) ? cache.results : cache.result, {
					'Force Fresh Results': force,
					'Is Image': image,
					'Number Requested': count,
					'Text Model': textModel || 'gpt-4.1-2025-04-14',
					'Image Model': imageModel || 'gpt-image-1',
					'Image if Referenced': image_url
				})
			return Array.isArray(query) ? cache.results : cache.result
		}
	},
	/**
	 * @author Jared
	 * @description Calculate the weight based on like, comment and share count
	 *
	 * @param {Number} [likes] - Like count
	 * @param {Number} [comments] - Comment count
	 * @param {Number} [shares] - Share count
	 * @param {Number} [followers] - Follower count
	 *
	 * @return {Number} - weight
	 */
	calculateEngagement: (likes, comments, shares, followers) => {
		const weightings = {
			likes: 1,
			comments: 3,
			shares: 6
		}

		let weight = 0

		weight += (likes / (followers / 100 || 1) || 0) * weightings.likes
		weight += (comments / (followers / 100 || 1) || 0) * weightings.comments
		weight += (shares / (followers / 100 || 1) || 0) * weightings.shares

		return weight
	},
	/**
	 * @author Jared
	 * @description Test a competitive analysis to see if it meets minimum viable requirements
	 *
	 * @param {Object} [analysis] - The Analysis object
	 *
	 * @return {String} - Human readable error message or null if clear
	 */
	checkCompetitiveAnalysis: analysis => {
		if (!analysis)
			return 'This competitive analysis is broken - it\'s probably best to remove this and start over.'
		if (analysis.parameters.competitors?.length < 1)
			return 'This competitive analysis requires at least one competitive brand.'

		if (analysis.parameters.competitors?.filter(competitor => competitor?.instagramUsername)?.length < 1)
			return 'This competitive analysis requires at least one competitive brand that includes an Instagram Account.'

		if (analysis.parameters?.mainClient?.histories && !analysis.parameters.mainClient.histories.instagram)
			return 'This competitive analysis requires the main client to have an Instagram Account with an adequate amount of history.'

		// if (analysis.parameters.competitors?.filter(competitor => competitor?.twitterUsername)?.length < 1)
		// 	return 'This competitive analysis requires at least one competitive brand that includes a Twitter Account with a Report.'

		return null
	},
	getKeywords: async () => {
		return await helperService.awaitFetch('/api/runner', {
			action: 'fetch',
			type: 'Categories'
		})
	},
	// eslint-disable-next-line no-undef
	getTopic: async topic => new Promise(resolve => {
		const ravenQuery = `from "Topics" where Keyword == "${topic}"`

		helperService.fetch('/api/ravenRunner', {
			action: 'fetch',
			query: ravenQuery
		}, updatedTopics => {
			resolve(updatedTopics?.[0] || null)
		})
	}),
	/**
	 * @author Jared
	 * @description Check the text pattern
	 *
	 * @param {String} topic - Accept text as string
	 *
	 * @return {String} - Retrieves the result of matching a string against a given pattern
	 */
	getTopicChildren: async topic => new Promise(resolve => {
		helperService.setThinking()
		const ravenQuery = `from "Topics" where parents = "${topic}" select Keyword`

		helperService.fetch('/api/ravenRunner', {
			action: 'fetch',
			query: ravenQuery
		}, updatedTopics => {
			resolve(updatedTopics)
			helperService.unsetThinking()
		})
	}),
	/**
	 * @author Jared
	 * @description Get the children of a given topic
	 *
	 * @param {String} topic - Name of the parent topic
	 *
	 * @return {Array} - Returns the topic children
	 */
	// eslint-disable-next-line no-undef
	getTopicChildrenCount: async topic => new Promise(resolve => {
		helperService.setThinking()
		const ravenQuery = `from "Topics" where parents = "${topic}"`

		helperService.fetch('/api/ravenRunner', {
			action: 'count',
			query: ravenQuery
		}, updatedTopics => {
			resolve(updatedTopics)
			helperService.unsetThinking()
		})
	}),
	/**
	 * @author Jared
	 * @description Get the meta stats of a given channel
	 *
	 * @param {Object} [query] - This can be a channel (instagram, twitter, tiktok) or a handle
	 *
	 * @return {Object} - Retrieves a list of statistics about the posts in question
	 */
	// eslint-disable-next-line no-undef
	getPostsStats: async query => new Promise(resolve => {
		helperService.setThinking()
		helperService.fetch('/api/ravenRunner', {
			action: 'aggregate',
			query
		}, postStats => {
			resolve(postStats)
			helperService.unsetThinking()
		})
	}),
	/**
	 * @author Jared
	 * @description Check the text pattern
	 *
	 * @param {String} text - Accept text as string
	 *
	 * @return {String} - Retrieves the result of matching a string against a given pattern
	 */
	getMentions: text => {
		const pattern = /\B@[a-z0-9_-]+/gi
		return text?.match(pattern) || []
	},
	/**
	 * @author Jared
	 * @description Email validator
	 *
	 * @param {String} email - The email string to check whether valid or not
	 *
	 * @return {Boolean} - Validated email
	 */
	validateEmail: email => {
		if (!email) return false

		let valid = true

		const emails = email.split(' ')

		emails.forEach(innerEmail => {
			if (innerEmail) {
				if (!(String(innerEmail)
					.toLowerCase()
					.match(
						/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
					)))
					valid = false
			}
		})
		return valid
		// return String(email)
		// 	.toLowerCase()
		// 	.match(
		// 		/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
		// 	)
	},
	/**
	 * @author Jared
	 * @description Get avatar for client
	 *
	 // * @param {Object} client - Client detail object
	 *
	 * @return {String} - Return image url as string
	 *
	 */
	fetchAvatarForClient: () => new Promise((resolve, reject) => {
		reject(null)
		// helperService.fetch('/api/runner', {
		// 	action: 'twitterImage',
		// 	username: client.handle,
		// 	accountId: client.account,
		// 	clientId: client._id
		// }, image => {
		// 	if (image) resolve(image.avatar)
		// 	else reject(null)
		// })
	}),
	/**
	 * @author Jared
	 * @description Update Cached Vars on both the Local and Server Levels
	 *
	 * @param {Object} vars - The global vars object
	 * @param {String} key - The key to update
	 * @param {String} localValue - The value to update locally, or on both if not distinct in serverValue
	 * @param {String} [serverValue] - The value to update on the server
	 *
	 * @return {Null}
	 */
	updateCachedVars: (vars, key, localValue, serverValue) => {
		vars[key] = localValue
		helperService.fetch('/api/runner', {
			action: 'updateCachedVars',
			key,
			value: serverValue || localValue
		})
	},
	/**
	 * @author Jared
	 * @description The most likely vertical for a given set of companies
	 *
	 * @param {Object} accounts - Accounts details
	 *
	 * @return {Array} - The most likely vertical for a given set of companies
	 */
	getVertical: accounts => {
		// eslint-disable-next-line no-undef
		return new Promise(async resolve => {
			await helperService.fetch('/api/runner', {
				action: 'fetch',
				type: 'Vertical',
				sort: {_id: -1}
			}, verticals => {
				const ravenQuery = `from "Accounts" where username in ("${accounts.split(',').map(handle => handle.trim())
					.join('", "')}") select vertical`
				helperService.fetch('/api/ravenRunner', {
					action: 'fetch',
					query: ravenQuery
				}, storedAccounts => {
					const accounts = storedAccounts.map(account => account['@metadata']['@id'])
					verticals.forEach(vertical => {
						vertical.count = vertical.accounts.filter(account => accounts.indexOf(account) > -1)?.length || 0
					})
					verticals.sort((a, b) => b.count - a.count)
					resolve(verticals[0])
				})
			})
			helperService.unsetThinking()
		})
	},
	/**
	 * @author Jared
	 * @description Filter clusters by team and vertical
	 *
	 * @param {Array} clusters
	 * @param {String} [team] - ID of the team to filter by - note you can only be on one team
	 * @param {Array} [verticals] - Array of verticals to filter by
	 *
	 * @return {Array} - The filtered clusters
	 */
	filterClusters: (clusters, team, verticals) => {
		return clusters?.filter(cluster => {
			if (verticals && cluster.verticals?.length > 0) {
				if (verticals.filter(vertical => cluster.verticals.findIndex(innerVertical => vertical === innerVertical) > -1).length ===
					0)
					return false
			}
			if (team && cluster.teams?.length > 0)
				if (cluster.teams.indexOf(team) === -1) return false

			return true
		})
	},
	/**
	 * @author Feroz
	 * @description check the string is parsable to json
	 *
	 * @param {String} rawString - raw string passes to the function
	 *
	 * @return {Boolean} - return the boolean on the basis of string parsable or not
	 */
	isParsableString: (rawString) => {
		try {
			JSON.parse(rawString)
		} catch (e) {
			return false
		}
		return true
	},
	formatBytes: (bytes, decimals = 2) => {
		if (!+bytes) return '0 Bytes'

		const k = 1024
		const dm = decimals < 0 ? 0 : decimals
		const sizes = ['Bytes', 'kb', 'mb', 'gb', 'tb', 'pb', 'eb', 'zb', 'yb']

		const i = Math.floor(Math.log(bytes) / Math.log(k))

		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`
	},
	generateUniqueFileName: (rawString) => {
		const type = rawString.split('.').pop().split('?')[0]
		return new mongoose.Types.ObjectId().toString() + '.' + type
	},
	/**
	 * @description Remove any tags that match our promotional clusters
	 *
	 * @param {array} tags
	 * @param {array} clusters
	 */
	removePromotionalTags: (tags, clusters) => {
		const promotionalClusters = clusters.filter(cluster => cluster.name.includes('giveaway')),
			promotionalTags = ['contest, sweepstakes & giveaway']

		promotionalClusters.forEach(cluster => {
			cluster.topics.forEach(topic => {
				const promotionalTagIndex = cluster.topics.findIndex(innerTopic => topic.topic === innerTopic.topic)

				if (promotionalTagIndex > -1)
					promotionalTags.push(topic.topic)
			})
		})
		return tags?.length > 0 && tags.filter(tag => promotionalTags.indexOf(tag) === -1)
	},
	/**
	 * @description Shuffle an array
	 *
	 * @param {array} array to shuffle
	 *
	 * @return {array} shuffled array
	 */
	shuffleArray: (array) => {
		const arrayCopy = [...array]

		for (let i = arrayCopy.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[arrayCopy[i], arrayCopy[j]] = [arrayCopy[j], arrayCopy[i]]
		}
		return arrayCopy
	},
	getArchetypes: async (handle, vars) => {
		const options = {
				// action: 'fetchImageAnalysis',
				query: `from \"Posts\" where search("username", "${handle}") order by created desc select tags, text, imageText, likes,
							 comments, image, created, username, platform limit 262`,
				clusters: vars.clusters,
				responseType: 'imagesOnly'
			},
			// content = await helperService.awaitFetch('/api/ravenRunner', options),
			reports = await helperService.analyzeImages(options),
			content = reports?.images,
			// copy of image service for now
			processReport = (report, excludes) => {
				const tags = []

				report?.images?.forEach(post => {
					if (post.tags && post.likes > -1) {
						post.tags.forEach(label => {
							const labelIndex = tags.findIndex(tag => tag.name === label.name),
								engagement = helperService.calculateEngagement(post.likes, post.comments, null, post.followers)

							if (!post.id)
								post.id = post['@metadata']['@id']

							if (labelIndex === -1) {
								tags.push({
									name: label.name,
									count: 1,
									score: label.score || 0,
									engagement: engagement,
									averageEngagement: engagement,
									ids: [post.id]
								})
							} else {
								tags[labelIndex].count++
								tags[labelIndex].score += label.score || 0
								tags[labelIndex].engagement += engagement
								tags[labelIndex].averageEngagement = tags[labelIndex].engagement / tags[labelIndex].count
								tags[labelIndex].ids.push(post.id)
							}
						})
					}
				})

				if (excludes) {
					const updatedExcludes = excludes.split(',').map(handle => handle.trim().toLowerCase())
					updatedExcludes.forEach(keyword => {
						const tagIndex = tags.findIndex(tag => tag.name?.toLowerCase() === keyword)
						if (tagIndex > -1) {
							tags.splice(tagIndex, 1)
						}
					})
				}

				report.averageLikes =
					report?.images?.reduce((total, next) => total + parseInt(next?.likes) || 0, 0) / report?.images?.length
				report.averageComments =
					report?.images?.reduce((total, next) => total + parseInt(next?.comments) || 0, 0) / report?.images?.length
				report.averageEngagement =
					report?.images?.reduce((total, next) => total + parseInt(next?.engagement) || 0, 0) / report?.images?.length

				report.totalEngagement = report?.images?.reduce((total, next) => total + parseFloat(next?.engagement) || 0, 0)
				report.count = report?.images?.length

				report.tags = tags.sort((b, a) => a.averageEngagement - b.averageEngagement)

				return report
			}

		if (!content) console.log('Failed to fetch data.', 'error')

		const filteredScores = helperService.filterOutliers(content?.map(image => image.engagement), 'jump') || []


		let updatedData = {
			images: content?.filter(image => filteredScores.indexOf(image.engagement) > -1)
		}
		updatedData.averageLikes =
			updatedData?.images?.reduce((total, next) => total + parseInt(next?.likes) || 0, 0) / updatedData?.images?.length
		updatedData.averageComments =
			updatedData?.images?.reduce((total, next) => total + parseInt(next?.comments) || 0, 0) / updatedData?.images?.length

		const data = processReport(updatedData),
			total = data.images.length,
			totalScore = data.images?.reduce((a, b) => a + helperService.calculateEngagement(b.likes, b.comments), 0),
			archetypeData = []

		data.images?.forEach(item => {
			item.tags.forEach(tag => {
				if (tag.source === 'Clusters') {
					if (tag.name.includes('archetype:')) {
						const cluster = archetypeData.find(cluster => cluster.name === tag.name.replace('archetype:', '').trim())
						if (cluster) {
							cluster.count++
							cluster.likes += item.likes
							cluster.comments += item.comments
							cluster.posts.push(item)
						} else {
							archetypeData.push({
								name: tag.name.replace('archetype:', '').trim(),
								count: 1,
								likes: item.likes,
								comments: item.comments,
								posts: [item]
							})
						}
					}
				}
			})
		})

		let used = []
		archetypeData.forEach(item => {
			item.engagement = helperService.calculateEngagement(item.likes, item.comments)
			item.engagementScore = (item.engagement / totalScore) * 100
			item.frequencyScore = (item.count / total) * 100

			item.posts = item.posts
				.sort((a, b) => helperService.calculateEngagement(b.likes, b.comments) - helperService.calculateEngagement(a.likes, a.comments))
				.filter(image => used.indexOf(image.id) === -1)
				.slice(0, 4)
			item.posts.forEach(post => used.push(post.id))
		})
		archetypeData.sort((a, b) => b.engagement - a.engagement)

		const archetypeObject = {},
			totalEngagement = archetypeData.reduce((total, next) => total + next.engagement, 0)

		archetypeData.forEach(archetype => {
			archetypeObject[helperServiceTextNumber.capitalize(archetype.name)] = archetype.engagement / totalEngagement * 100
		})

		helperServiceVars.vars.archetypes.forEach(archetype => {
			if (!archetypeObject[archetype])
				archetypeObject[archetype] = 0
		})
		return archetypeObject
	},
	/**
	 * @author Prashant
	 * @description Image analyses
	 *
	 * @param {Object} bodyParams - Object to pass to the lambda function with queries, clusters, accounts, etc., and existing parameters.
	 *                              Add responseType = 'tagsOnly' to get only tags.
	 *                              Add responseType = 'imagesOnly' to get only images.
	 *                              Otherwise, it will return tags and images.
	 *
	 * @return {Array} - Returns an Array of image analyses.
	 */
	analyzeImages: async (bodyParams) => {
		return await helperService.awaitFetch('/api/ravenRunner', {
			action: 'analyzeImages',
			bodyParams: bodyParams
		})
	},
	/**
	 * @author Jared
	 *
	 * @description Check if an instagram handle is valid
	 *
	 * @param {String} handle - The instagram handle to check
	 *
	 */
	checkInstagramHandle: async (handle) => {
		return await helperService.awaitFetch('/api/runner', {
			action: 'checkInstagramHandle',
			handle
		})
	},
	/**
	 * @author Jared
	 *
	 * @description Check if an tiktok handle is valid
	 *
	 * @param {String} handle - The tiktok handle to check
	 *
	 */
	checkTikTokHandle: async (handle) => {
		return await helperService.awaitFetch('/api/runner', {
			action: 'checkTikTokHandle',
			handle
		})
	},
	handleGAEvent: (event, payload) => {
		ReactGA.gtag('event', event, payload)
	},
	s3: async (task, params) => {
		return await helperService.awaitFetch('/api/runner', {
			action: 's3',
			task,
			params
		})
	},
	lambda: async params => {
		return await helperService.awaitFetch('/api/runner', {
			action: 'lambda',
			params
		})
	},
	setupLambda: async params => {
		return await helperService.awaitFetch('/api/runner', {
			action: 'setupLambda',
			params
		})
	},
	setupUserHandle: async (options, ownHandles, alert) => {
		helperService.log('SETUP', `Social handle addition process started for handle: ${ownHandles.instagram}`, options.user._id,
			`${options?.email ? options?.email : 'Unknown User'}`)
		const topicExcludes = ['human-centered', 'human centered', 'head', 'adult', 'accessories', 'introducing', 'the link', 'purchase']
		const addChannels = async (handles) => {
			try {
				const promises = []
				for (let handle of handles) {
					// eslint-disable-next-line no-undef
					promises.push(new Promise(async resolve => {
						const options = {
							FunctionName: 'ag-setup-handler-v2-dev-run',
							Payload: JSON.stringify({handle: handle})
						}
						const data = await helperService.setupLambda(options)
						console.log('data at setup complete', data)
						if (data.result.statusCode === 200) {
							alert(`Fetched posts for handle ${handle} successfully`, 'success', 4000)
						} else {
							if (data?.result?.body) {
								console.log(JSON.parse(data.result.body).message)
								alert(`Can not setup your handle: ${handle}, please try again`, 'warning', 4000)
							} else {
								alert(`Can not setup your handle: ${handle},, please try again`, 'warning', 4000)
							}
						}
						resolve()
					}))
				}
				await Promise.all(promises)
			} catch (error) {
				console.log(error)
			}
		}
		const gatherAccountData = async () => {
			let accounts = []

			const accountsToProcess = [],
				tagCountThreshold = 2
			if (ownHandles.instagram)
				accountsToProcess.push({
					channel: 'instagram',
					handle: ownHandles.instagram.split(/[ ,]+/)[0].trim().toLowerCase(),
					type: 'owned'
				})

			// check each handle
			const failures = []
			// checkerPromises = []
			for (const account of accountsToProcess) {
				// eslint-disable-next-line no-undef
				// checkerPromises.push(new Promise(async resolve => {
				if (account.channel === 'instagram') {
					const valid = await helperService.checkInstagramHandle(account.handle)
					if (!valid?.valid) {
						failures.push(account.handle)
						alert('No Instagram handle found for ' + account.handle, 'error', 5000)
						helperService.log('SETUP', `No Instagram handle found for: ${ownHandles.instagram}`, options.user._id,
							`${options.email ? options.email : 'Unknown User'}`)
						return
					}
				}
			}
			if (accountsToProcess.filter(handle => handle.channel === 'instagram')?.length > 0) {
				const instagramAccounts = await helperService.awaitFetch('/api/lakeRunner', {
					action: 'fetch',
					type: 'InstagramAccount',
					sort: {_id: -1},
					fields: {name: 1, handle: 1},
					query: {handle: {$in: accountsToProcess.filter(handle => handle.channel === 'instagram').map(instagram => instagram.handle)}},
					limit: 10
				})
				if (instagramAccounts?.length < accountsToProcess.filter(account => account.channel === 'instagram')?.length) {
					const handlesToProcess = []
					for (const handle of accountsToProcess.filter(handle => handle.channel === 'instagram')) {
						if (instagramAccounts.filter(account => account.handle === handle.handle)?.length === 0) {
							handlesToProcess.push(handle.handle)
						}
					}
					helperService.log('SETUP', `Adding Instagram handles: ${handlesToProcess.join(', ')}`, options.user._id,
						`${options.email ? options.email : 'Unknown User'}`)
					await addChannels(handlesToProcess, 'instagram')
				}
			}

			const promises = []

			// now we should have accounts and posts
			for (const account of accountsToProcess) {
				// eslint-disable-next-line no-undef
				promises.push(new Promise(async resolve => {
					const accountData = await helperService.awaitFetch('/api/lakeRunner', {
						action: 'fetchOne',
						type: helperServiceTextNumber.capitalize(account.channel) + 'Account',
						fields: {
							website: 1,
							verified: 1,
							posts: 1,
							pic: 1,
							name: 1,
							link: 1,
							handle: 1,
							id: 1,
							following: 1,
							followers: 1,
							bio: 1
						},
						query: {handle: account.handle}
					})

					if (!accountData) {
						alert('Error reading account data for ' + account.handle, 'warning', 5000)
					} else {
						const query = `from "Posts" where search("username", "${account.handle}") and platform == "${account.channel}" order by created desc limit 262`,
							report = await helperService.analyzeImages({query: query, clusters: options?.clusters, excludes: topicExcludes.join(',')})
						if (report) {
							if (report?.archetypes?.length < 1 || report?.intents?.length < 1 || report?.tags?.length < 5 || report?.images?.length <
								15) {
								// setHasFewerPost(true)
								helperService.log('SETUP', `Limited data found for ${account.handle}`, options.user._id,
									`${options.email ? options.email : 'Unknown User'}`)
							}
							// const brandText = await helperService.askGPT(vars,
							// 		`Given these below post of ${account.handle} \n${report.images?.slice(0, 10)
							// 			.map(post => post.text + '\n')} write a two-sentence summary of my brand ${account.handle}`, false, false),
							// 	brandImage = await helperService.askGPT(vars, `Given these below images \n${report.images?.slice(0, 10)
							// 		.map(post => post.image +
							// 				'\n')} write a two-sentence description of what most of my brand ${account.handle} images look like`,
							// 	false, false)

							const brandText = await helperService.askGPT(options,
									`Given these below post of ${account.handle} \n${report.images?.slice(0, 10)
										.map(post => post.text + '\n')} write a two-sentence summary of my brand ${account.handle}`, false, false),
								brandImage = await helperService.askGPT(options,
									`Given these below images \n${report.images?.slice(0, 10)
										.map(post => post.image +
											'\n')} write a two-sentence description of what most of my brand ${account.handle} images look like`,
									false, false)

							report.imagesCount = report.images.length
							// delete report.images
							report.tags = report.tags?.filter(tag => tag.count > tagCountThreshold)?.slice(0, 100)

							accounts.push({
								handle: account.handle,
								type: account.type,
								channel: account.channel,
								name: accountData.name,
								avatar: accountData.pic,
								data: accountData,
								brandText: brandText,
								brandImage: brandImage,
								report
							})
							resolve()
						} else {
							helperService.log('SETUP', `No data found for ${account.handle}`,
								options.user._id, `${options.email ? options.email : 'Unknown User'}`)
							alert('No data found for ' + account.handle, 'warning', 5000)
							resolve()
						}
					}
				}))
			}
			await Promise.all(promises)
			if (accounts.length > 0) {
				let preferences = options.preferences ? options.preferences : {}
				preferences.accounts = accounts
				preferences.subspace = options.subspace
				preferences.lastUpdated = new Date()
				await helperService.updateProfile(options.user, 'preferences', preferences)
				helperService.log('SETUP', 'Social data added successfully', options.user._id, `${options.email ? options.email : 'Unknown User'}`)
				alert('Social data added successfully', 'success', 5000)
				// setLoading(false)
			} else {
				alert('Something went wrong, Please try again with corrected handles', 'error', 5000)
			}
		}
		return gatherAccountData()
	},
	/**
	 * @author Prashant
	 * @description Get proper user name or email
	 */
	loggingUserDetails: (options, vars) => {
		if (vars?.user?.firstName) {
			return `${vars?.user?.email}`
		} else if (options?.user?.firstName) {
			return `${options?.email}`
		} else {
			return vars?.user?.email ? `${vars.user.email}` : `${options?.email ? options?.email : 'Unknown User'}`
		}
	},
	isPublishLimitExceeded: (currentPublishedCount, publishLimit, publishedMonth) => {
		return (publishedMonth === new Date().getMonth()) && currentPublishedCount >= publishLimit
	},
	/**
	 * @author Jared
	 * @description Return the number of posts available for scheduling
	 *
	 * @param vars
	 * @param {Number} [month] - zero-based

	 * @return {Promise<void>}
	 */
	getRemainingPosts: async (vars, month) => {
		const
			tier = helperServiceAuth.tier(vars),
			day = new Date(vars.team?.subscriptions[0]?.createdDate).getDate(),
			rangeQuery =
				{
					scheduledDateTime: {
						$gte: format(new Date(new Date().getFullYear(), month || new Date().getMonth(), day || 1), 'yyyy-MM-dd'),
						$lt: format(new Date(new Date().getFullYear(), (month || new Date().getMonth()) + 1, day || 1), 'yyyy-MM-dd')
					}
				},
			usedPosts = await helperService.get({
				ownerId: vars.user._id,
				status: {$in: [ 'posted', 'scheduled']},
				...rangeQuery
			}, 'Post', {caption: 1}),
			usedPostCount = helperServiceTextNumber.uniqueArray(usedPosts, 'caption')?.length || 0,
			getAllowable = () => {
				if (vars?.team?.postOverride) {
					return vars?.team?.postOverride
				} else {
					if (tier === 1)
						return 3
					else if (tier === 2) // ten posts
						return 10
					else if (tier === 3) // thirty posts
						return 30
				}
			}

		return getAllowable() - usedPostCount
	},
	/**
	 * @author Jared
	 * @description Return the number of posts available as in to cmplete the month
	 *
	 * @param vars
   * @param {Date} start - The start date for the range
   * @param {Date} end - The end date for the range

	 * @return {Promise<void>}
	 */
	getPostsToGenerateCount: async (vars, start, end) => {
		const day = new Date(vars.team?.subscriptions[0]?.createdDate).getDate(),
			posts = await helperService.get({
				ownerId: vars.user._id.toString(),
				scheduledDateTime: {
					$gte: format(start, 'yyyy-MM-dd'),
					$lt: format(end, 'yyyy-MM-dd')
				},
			}, 'Post', {scheduledDateTime: 1, status: 1})

		let count = 0
		const weekendPostsAllowed = vars.user.preferences.weekendPosts

		for (let day = new Date(start); day <= end; day.setDate(day.getDate() + 1)) {
			const isWeekend = day.getDay() === 0 || day.getDay() === 6 // 0 = Sunday, 6 = Saturday

			if (!weekendPostsAllowed && isWeekend) {
				continue // Skip weekends if not allowed
			}

			const hasPost = posts.filter(post => post.status !== 'extra' && post.status !== 'canceled').some(post => {
				const postDate = new Date(post.scheduledDateTime)
				return postDate.toDateString() === day.toDateString()
			})

			if (!hasPost) {
				count++
			}
		}

		// add about 20% extras buffer if we don't have that many already
		const extras = posts.filter(post => post.status === 'extra')?.length
		if (extras < Math.floor(count * 0.2))
			count += Math.floor(count * 0.2) - extras

		if (count > (helperServiceAuth.tier(vars) === 2 ?  20 : 30))
			count = 30 // never give more than 30
		if (count > vars?.user?.preferences?.postTotal)
			count = vars?.user?.preferences?.postTotal // never give more than requested
		if (count <= 0)
			return 0

		return count
	},
	updateTeamForPublishAssets: async (team, publishAssets, publishedMonth) => {
		team.publishedCount = publishAssets
		team.publishedMonth = publishedMonth
		await helperService.awaitFetch('/api/runner', {
			action: 'manage',
			type: 'Team',
			object: team
		})
	},
	/**
	 * @author Jared
	 * @description Either get or set the team's usage
	 *
	 * @param {Object} vars
	 * @param {Boolean} [add]
	 * @return {Promise<number|number>}
	 */
	remainingPosts: async (vars, add) => {
		const tier = helperServiceAuth.tier(vars)
		let limit = 3
		if (tier === 2)
			limit = 10
		else if (tier > 2)
			limit = 30


		const month = new Date().getMonth(),
			team = await helperService.awaitFetch('/api/runner', {
				action: 'fetchOne',
				type: 'Team',
				query: {_id: vars.user?.team._id}
			})

		if (team && (!team.publishedCount || !team.publishedMonth)) {
			team.publishedCount = 0
			team.publishedMonth = month
		}
		if (team?.publishedMonth === month) {
			if (add) {
				team.publishedCount++
				await helperService.awaitFetch('/api/runner', {
					action: 'manage',
					type: 'Team',
					object: team
				})
			}
			return limit - team?.publishedCount
		} else {
			team.publishedMonth = month
			team.publishedCount = 1
			await helperService.awaitFetch('/api/runner', {
				action: 'manage',
				type: 'Team',
				object: team
			})
			return limit
		}
	},
	/**
	 * @description Capture the enter key (centralize for later logging)
	 *
	 * @param {Object} event
	 * @param {Function} action
	 * @param {String} variable
	 * @param {Function} [log]
	 *
	 *
	 * @return null
	 */
	enterGo: (event, action, variable) => {
		if (event.key === 'Enter') {
			action(variable)
		}
	},

	/**
	 * @author Prashant
	 * @description To know if it's a paid plan at the start of application
	 *
	 * @param {String} planName
	 * @return {Boolean}
	 */
	isValidPlanToRenew: (planName) => {
		if (planName === 'AGGIE Solo' || planName.toLowerCase() === 'aggie light') {
			return false
		} else {
			return true
		}
	},

	/**
	 *
	 * Social Connector Stuff Starts Here
	 *
	 */

	/**
	 * @author Jared
	 * @description Check if a user has a live instagram token
	 *
	 * @param {Object} vars - the data object including the user
	 *
	 * @return {Boolean}
	 */
	checkMetaToken: async (vars) => {
		const result = await helperService.awaitFetch('/api/connectors/meta', {
			action: 'checkToken',
			user: vars.user._id
		})
		return (result?.success === true)
	},
	/**
	 * @author Jared
	 * @description Post to Instagram
	 *
	 * @param {Object} vars - the data object including the user
	 * @param {Object} image - the image object
	 * @param {Array}} images - if multiple
	 * @param {String} caption - the caption for the post
	 * @param {String} which - which account to post to - index of
	 *
	 * @return {Object}
	 */
	postToInstagram: async (vars, image, images, caption, accounts) => {
		// Loop through all accounts selected and publish. Return array of results to display alerts
		return await Promise.all(
			accounts.map(async (account) => {
				return await helperService.awaitFetch('/api/connectors/meta', {
					action: 'postToInstagram',
					token: vars.user.preferences.tokens.meta.token,
					pageId: account.subacc,
					instagramId: account.id,
					image,
					images,
					caption
				})
			})
		)
	},
	/**
	 * @author Jared
	 * @description Post to Facebook
	 *
	 * @param {Object} vars - the data object including the user
	 * @param {Object} image - the image object
	 * @param {String} caption - the caption for the post
	 * @param {String} link - the link for the post
	 * @param {Number} which - which account to post to - index of
	 *
	 * @return {Object}
	 */
	postToFacebook: async (vars, image, caption, link, which) => {
		const result = await helperService.awaitFetch('/api/connectors/meta', {
			action: 'postToFacebook',
			token: vars.user.preferences.tokens.meta.token,
			pageId: vars.user.preferences.tokens.meta.accounts[which].id,
			image,
			message: caption,
			link
		})
		return result
	},

	/**
	 * @author Jared
	 * @description Check if a user has a live LinkedIn token
	 *
	 * @param {Object} vars - the data object including the user
	 *
	 * @return {Boolean}
	 */
	checkLinkedInToken: async (vars) => {
		const result = await helperService.awaitFetch('/api/connectors/linkedIn', {
			action: 'checkToken',
			user: vars.user._id
		})
		return (result?.success === true)
	},

	/**
	 * @author Jared
	 * @description Post to LinkedIn
	 *
	 * @param {Object} vars - the data object including the user
	 * @param {Object} image - the image object
	 * @param {String} caption - the caption for the post
	 * @param {String} link - the link for the post
	 *
	 * @return {Object}
	 */
	postToLinkedIn: async (vars, image, caption, link, which) => {
		try {

			const result = await helperService.awaitFetch('/api/connectors/linkedIn', {
				action: 'postToLinkedIn',
				token: vars.user.preferences.tokens.linkedIn.access_token,
				user: vars.user.preferences.tokens.linkedIn.profile.sub,
				image,
				message: caption,
				link
			})

			return result

		} catch (error) {
			console.log(error)
		}
	},

	/**
	 * @author Jared
	 * @description Look through the user tokens and gather stats for each of the channels and accounts listed then save to the user stats object
	 *
	 * @param {Object} vars - the data object including the user
	 *
	 * @return {Object}
	 */
	getStatistics: async (vars) => {
		const tokens = vars.user.preferences.tokens,
	        userStatsRun = {
		        user: vars.user._id,
		        organization: vars.user.team,
		        date: new Date,
		        stats: []
	        }

		if (!tokens || tokens.length === 0) return

		for (const [channel, tokenData] of Object.entries(tokens)) {
			try {
				// Call the appropriate stats function based on the channel
				switch (channel.toLowerCase()) {
				case 'linkedin':
					// for now, skip the personal account (account[0])
					for (const organization of tokenData.organizations) {
						const accountStats = await helperService.getLinkedInStatistics(vars, tokenData.organizations.findIndex(innerAccount => innerAccount.orgID === organization.orgID) + 1)
						userStatsRun.stats.push({
							channel,
							accountId: organization.orgID,
							accountName: organization.orgName,
							posts: accountStats,
						})
					}
					break
				case 'twitter':
					const accountStats = await helperService.getTwitterStatistics(vars)
					userStatsRun.stats.push({
						channel,
						accountId: tokenData.userId,
						accountName: tokenData.screenName,
						posts: accountStats,
					})
					break
				case 'meta':
					for (const account of tokenData.accounts.filter(innerAccount => innerAccount.type === 'FB')) {
						const accountStats = await helperService.getFacebookStatistics(vars, tokenData.accounts.filter(innerAccount => innerAccount.type === 'FB')
							.findIndex(innerAccount => innerAccount.id === account.id))
						userStatsRun.stats.push({
							channel: 'facebook',
							accountId: account.id,
							accountName: account.name,
							posts: accountStats,
						})
					}
					for (const account of tokenData.accounts.filter(innerAccount => innerAccount.type === 'IG')) {
						const accountStats = await helperService.getInstagramStatistics(vars, tokenData.accounts.filter(innerAccount => innerAccount.type === 'IG')
							.findIndex(innerAccount => innerAccount.id === account.id))
						userStatsRun.stats.push({
							channel: 'instagram',
							accountId: account.id,
							accountName: account.name,
							posts: accountStats,
						})
					}
					break
				default:
					console.warn(`Unsupported channel: ${channel}`)
					continue
				}
			} catch (error) {
				console.error(`Error gathering stats for ${channel}:`, error)
			}
		}

		helperService.manage(userStatsRun, 'UserStatsRun')
		return userStatsRun
	},
	/**
	 * @author Jared
	 * @description Get Facebook Stats
	 *
	 * @param {Object} vars - the data object including the user
	 * @param {Number} [which] - which account if multiple
	 *
	 * @return {Object}
	 */
	getFacebookStatistics: async (vars, which) => {
		// get token
		const account = vars.user.preferences.tokens?.meta?.accounts?.filter(account => account.type === 'FB')?.[which]
		if (!account) return
		try {
			const result = await helperService.awaitFetch('/api/connectors/meta', {
				action: 'getFacebookStatistics',
				token: vars.user.preferences.tokens.meta.token,
				account: account.id
			})

			return result?.stats?.data.map(post => ({
				id: post.id,
				caption: post.message,
				images: [post.attachments?.data?.[0]?.media?.image?.src],
				likesCount: post.likes?.summary?.total_count,
				likes: post.likes?.data,
				commentsCount: post.comments?.summary?.total_count,
				comments: post.comments?.data,
				sharesCount: post.shares?.count,
				date: new Date(post.created_time)
			}))
		} catch (error) {
			console.log(error)
		}
	},

	/**
	 * @author Jared
	 * @description Get Facebook Stats
	 *
	 * @param {Object} vars - the data object including the user
	 * @param {Number} [which] - which account if multiple
	 *
	 * @return {Object}
	 */
	getInstagramStatistics: async (vars, which) => {
		// get token
		const account = vars.user.preferences.tokens?.meta?.accounts?.filter(account => account.type === 'IG')?.[which]
		if (!account) return
		try {
			const result = await helperService.awaitFetch('/api/connectors/meta', {
				action: 'getInstagramStatistics',
				token: vars.user.preferences.tokens.meta.token,
				account: account.id
			})

			return result?.stats?.data.map(post => ({
				id: post.id,
				caption: post.caption,
				images: [post.media_url],
				likesCount: post.like_count,
				likes: [],
				commentsCount: post.comments_count,
				comments: [],
				sharesCount: null,
				date: new Date(post.timestamp),
			}))

		} catch (error) {
			console.log(error)
		}
	},

	/**
	 * @author Jared
	 * @description Get LinkedIn Stats
	 *
	 * @param {Object} vars - the data object including the user
	 * @param {Number} [which] - which account if multiple
	 *
	 * @return {Object}
	 */
	getLinkedInStatistics: async (vars, which) => {
		// get token
		const account = which === 0 || which === null ? vars.user.preferences.tokens?.linkedIn?.profile?.sub : vars.user.preferences.tokens?.linkedIn.organizations?.[which - 1]?.organizationalTarget

		try {
			const result = await helperService.awaitFetch('/api/connectors/linkedIn', {
				action: 'getLinkedInStatistics',
				token: vars.user.preferences.tokens.linkedIn.access_token,
				account: account
			})

			// return result?.stats?.elements.map(post => ({
			// 	id: post.id,
			// 	caption: post.commentary,
			// 	images: [post.media_url],
			// 	likesCount: post.reactions?.length,
			// 	likes: post.reactions,
			// 	commentsCount: post.comments?.length,
			// 	comments: post.comments,
			// 	sharesCount: null,
			// 	date: new Date(post.publishedAt),
			// }))

			return result?.stats?.elements.map(post => ({
				id: post.id,
				caption: post.commentary,
				images: [post.media_url],
				likesCount: post.likesCount,
				likes: post.likes || [],
				commentsCount: post.commentsCount,
				comments: post.comments || [],
				sharesCount: post.sharesCount,
				impressionsCount: post.impressionsCount,
				uniqueImpressionsCount: post.uniqueImpressionsCount,
				clickCount: post.clicksCount,
				date: new Date(post.publishedAt),
			}))
		} catch (error) {
			console.log(error)
		}
	},

	/**
	 * @author Jared
	 * @description Get Twitter Stats
	 *
	 * @param {Object} vars - the data object including the user
	 * @param {Number} [which] - which account if multiple
	 *
	 * @return {Object}
	 */
	getTwitterStatistics: async (vars) => {
		// get token
		const account = vars.user.preferences.tokens?.twitter
		if (!account) return

		try {
			const result = await helperService.awaitFetch('/api/connectors/twitter', {
				action: 'getTwitterStatistics',
				token: account.access_token,
				secret: account.access_secret,
				account: account.userId
			})

			return result?.stats?.map(post => ({
				id: post.id,
				caption: post.text,
				images: post.includes?.media?.[0]?.preview_image_url ? [post.includes?.media?.[0]?.preview_image_url] : null,
				likesCount: post.public_metrics?.like_count,
				likes: [],
				commentsCount: post.public_metrics?.reply_count,
				comments: [],
				sharesCount: post.public_metrics?.retweet_count,
				date: new Date(post.created_at)
			}))

		} catch (error) {
			console.log(error)
		}
	},
	/**
   * @author Jared
   * @description Get Avatar for user where you can...
   *
   * @param {Object} vars - the data object including the user
   */
	getAvatar: vars => {
		// if (vars.user.preferences?.tokens?.meta?.accounts?.filter(account => account.type === 'IG')?.length > 0)
		// 	return vars.user.preferences.tokens.meta.accounts?.filter(account => account.type === 'IG')?.[0].profile_pic
		if (vars.user.preferences.tokens.linkedIn.profile.picture)
			return vars.user.preferences.tokens.linkedIn.profile.picture
		return '/images/avatar.svg'
	},
	/**
   * @author Jared
   * @description Get the available accounts for the user
   *
   * @param vars
   * @return {*[]}
   */
	getAvailableAccounts: vars => {
		const tokens = vars.user?.preferences?.tokens,
			accounts = []

		if (!tokens || tokens.length === 0) return []

		// by channel
		if (tokens.meta) {
			tokens.meta.accounts.filter(account => account.type === 'FB').forEach(account => {
				accounts.push({
					type: 'facebook',
					icon: 'fab fa-facebook',
					name: account.name,
					id: account.id,
				})
			})
			tokens.meta.accounts.filter(account => account.type === 'IG').forEach(account => {
				accounts.push({
					type: 'instagram',
					icon: 'fab fa-instagram',
					name: account.name,
					id: account.id,
				})
			})
		}
		if (tokens.twitter) {
			accounts.push({
				type: 'twitter',
				icon: 'fab fa-twitter',
				name: tokens.twitter.screenName,
				id: tokens.twitter.userId,
			})
		}
		if (tokens.tiktok) {
			accounts.push({
				type: 'tiktok',
				icon: 'fab fa-tiktok',
				name: tokens.tiktok.username,
				id: tokens.tiktok.username,
			})
		}
		if (tokens.linkedIn) {
			accounts.push({
				type: 'linkedin',
				icon: 'fab fa-linkedin',
				name: tokens.linkedIn.profile.name,
				id: tokens.linkedIn.profile.sub
			})
			if (tokens.linkedIn.organizations) {
				tokens.linkedIn.organizations.forEach(account => {
					accounts.push({
						type: 'linkedin',
						icon: 'fab fa-linkedin',
						name: account.orgName,
						id: account.orgID
					})
				})
			}
		}

		return accounts
	},
	/**
   * @author Jared
   * @description Update multiple posts
   *
   * @param {Object} post - The post id to update
   * @param {Array} accounts - The accounts to connect in the format {channel: 'instagram', id: '1234567890'}
   */
	approveMultiplePosts: async(postIds, accounts, vars) =>  {
		for (const postId of postIds) {
			const post = await helperService.awaitFetch('/api/runner', {
					action: 'fetchOne',
					type: 'Post',
					query: {_id: postId}
				}),
				posts = [post]

			//!todo Check per account - for now just skip scheduled
			if (!post || post.status === 'scheduled') continue // Skip if the post is not found

			posts[0].status = 'scheduled' // Default set new posts to be scheduled
			posts[0].scheduledChannel = accounts[0].channel
			posts[0].scheduledUsername = accounts[0].id

			if (accounts.length > 1) {
				// for every post after the first one, create a duplicate and add it to the posts array
				for (let i = 1; i < accounts.length; i++) {
					const duplicatedPost = {...post}
					duplicatedPost.scheduledChannel = accounts[i].channel
					duplicatedPost.scheduledUsername = accounts[i].id
					duplicatedPost.status = 'scheduled' // Default set new posts to be scheduled
					duplicatedPost.parent = post._id // Set the parent post ID
					delete duplicatedPost._id // Ensure we create a new post id
					posts.push(duplicatedPost)
				}
			}

			for (const post of posts) {
				await helperService.manage(post, 'Post')
			}
		}
	},

	/**
	 * @author Jared
	 *
	 * @description Get team files
	 *
	 * @param {Object} teamId - id of the team
	 * @param {Object} folder - folder to get
	 *
	 * @return {Object} - file list
	 */
	getTeamImageFolders: async (teamId, folder) => {
		const query = folder ? {organization: teamId, tags: folder} : {organization: teamId}
		return await helperService.awaitFetch('/api/runner', {
			action: 'fetch',
			type: 'OrganizationAssets',
			query
		})
	},
	/**
	 * @author Prashant
	 * @description Check if the current environment matches any of the specified environments
	 *
	 * @param {Array<String>} environments - An array of environment names to check against (e.g., ['prod', 'dev', 'localhost'])
	 *
	 * @return {Promise<Boolean>} A promise that resolves to true if the current hostname matches any of the specified environments, otherwise false
	 */
	env: async (environments) => {
		const hostname = window?.location.hostname
		const environmentKeywords = {
			prod: ['app'],
			dev: ['dev'],
			localhost: ['localhost', '127.0.0.1'],
		}

		return environments.some(env => {
			if (environmentKeywords[env]) {
				return environmentKeywords[env].some(keyword => hostname.includes(keyword))
			}
			return false
		})
	},
	/**
	 * @author Jared
	 * @description manage any object
	 *
	 * @param {Object} object - The object to manage
	 * @param {String} type - The type of object to manage
	 */
	manage: async (object, type) => {
		return await helperService.awaitFetch('/api/runner', {
			action: 'manage',
			type,
			object
		})
	},
	/**
	 * @author Jared
	 * @description get any object
	 *
	 * @param {Object} query - The query
	 * @param {String} type - The type of object to manage
	 * @param {Object} [fields] - The fields to return
	 * @param {Number} [limit] - The limit of objects to return
	 * @param {Object} [sort]
	 */
	get: async (query, type, fields, limit, sort) => {
		const options = {
			action: 'fetch',
			type,
			query
		}
		if (fields) options.fields = fields
		if (limit) options.limit = limit
		if (sort) options.sort = sort

		return await helperService.awaitFetch('/api/runner', options)
	},

	getOne: async (query, type, fields) => {
		const options = {
			action: 'fetchOne',
			type,
			query
		}
		if (fields) options.fields = fields

		return await helperService.awaitFetch('/api/runner', options)
	},
	/**
	 * @author Jared
	 * @description Get a set a cache value
	 *
	 * @param {String} [organizationId] - The organization ID
	 * @param {String} [ownerId] - The owner ID
	 * @param {String} type - The type of object to cache
	 * @param {Object} [object] - The object to cache - if saving
	 * @param {Number} [timeout] - The lifetime in milleseconds of the cache
	 * @param {Number} [cacheUpdateId] - Cache id to update cache
	 *
	 * @return {Promise<Boolean>} A promise that resolves to true if the current hostname is localhost, otherwise false
	 */
	cache: async(organizationId, ownerId, type, object, timeout, cacheUpdateId) => {
		if (cacheUpdateId && object) {
			return await helperService.awaitFetch('/api/runner', {
				action: 'manage',
				type: 'Cache',
				object: {
					_id: cacheUpdateId,
					organizationId,
					ownerId,
					type,
					object,
					date: new Date()
				}
			})
		} else if (object) {
			return await helperService.awaitFetch('/api/runner', {
				action: 'manage',
				type: 'Cache',
				object: {
					organizationId,
					ownerId,
					type,
					object,
					date: new Date()
				}
			})
		} else {
			const cache = await helperService.awaitFetch('/api/runner', {
				action: 'fetchOne',
				type: 'Cache',
				query: {organizationId, ownerId, type,
					date: {
						$gte: new Date(new Date().getTime() - timeout)
					}
				}
			})
			return cache?.object
		}
	},
	/**
	 * @author Jared
	 * @description Clean out any handle
	 *
	 * @param {String} [handle] - The handle to clean
	 *
	 * @return {String} Just the handle
	 */
	cleanHandle: (handle) => {
		if (handle.includes('instagram.com/')) return handle.split('instagram.com/')[1]?.split('?')[0].split('/')[0]
		if (handle.indexOf('@') === 0) return handle.slice(1)

		return handle
	},
	/**
	 * @author Jared
	 * @description Set of Topic Functions
	 *
	 */

	/**
	 * @param {String} topicName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} [page]
	 *
	 * @return {Promise<void>}
	 */
	toggleTopic: async(topicName, vars, alert, page) => {
		if (!vars.user.preferences.topics)
			vars.user.preferences.topics = []
		const topicIndex = vars.user.preferences.topics.findIndex(topic => topic.name === topicName)
		if (topicIndex > -1) {
			vars.user.preferences.topics.splice(topicIndex, 1)
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has removed the topic ${topicName}`, page || 'Profile', vars.user)
			alert('Topic removed', 'success', 4000)
		} else {
			vars.user.preferences.topics.push({name: topicName})
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has added the topic ${topicName}`, page || 'Profile',  vars.user)
			alert('Topic added', 'success', 4000)
		}
	},
	/**
	 * @param {String} topicName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	addTopic: async(topicName, vars, alert, page) => {
		if (!vars.user.preferences.topics)
			vars.user.preferences.topics = []
		const topicIndex = vars.user.preferences.topics.findIndex(topic => topic.name === topicName)
		if (topicIndex === -1) {
			vars.user.preferences.topics.push({name: topicName})
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has added the topic ${topicName}`, page || 'Profile',  vars.user)
			alert('Topic added', 'success', 4000)
		}
	},
	/**
	 * @param {String} topicName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	removeTopic: async(topicName, vars, alert, page) => {
		if (!vars.user.preferences.topics)
			vars.user.preferences.topics = []
		const topicIndex = vars.user.preferences.topics.findIndex(topic => topic.name === topicName)
		if (topicIndex > -1) {
			vars.user.preferences.topics.splice(topicIndex, 1)
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has removed the topic ${topicName}`, page || 'Profile',  vars.user)
			alert('Topic removed', 'success', 4000)
		}
	},
	/**
	 * @param {String} topicName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	toggleTopicStar: async(topicName, vars, alert, page) => {
		if (!vars.user.preferences.topics)
			vars.user.preferences.topics = []
		const topicIndex = vars.user.preferences.topics.findIndex(topic => topic.name === topicName)
		if (topicIndex > -1) {
			vars.user.preferences.topics[topicIndex].starred = !vars.user.preferences.topics[topicIndex].starred
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has ${vars.user.preferences.topics[topicIndex].starred ? 'starred' : 'unstarred'} the topic ${topicName}`, page || 'Profile',  vars.user)
			alert('Topic starred', 'success', 4000)
		}
	},

	/**
 * @author Jared
 * @description Set of Hashtag Functions
 *
 */

	/**
	 * @param {String} hashtagName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} [page]
	 *
	 * @return {Promise<void>}
	 */
	toggleHashtag: async (hashtagName, vars, alert, page) => {
		if (!vars.user.preferences.hashtags || vars.user.preferences.hashtags === true || vars.user.preferences.hashtags === false) // vestigial fix
			vars.user.preferences.hashtags = []
		const hashtagIndex = vars.user.preferences.hashtags.findIndex(hashtag => hashtag.name === hashtagName)
		if (hashtagIndex > -1) {
			vars.user.preferences.hashtags.splice(hashtagIndex, 1)
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has removed the hashtag ${hashtagName}`, page || 'Profile', vars.user)
			alert('Hashtag removed', 'success', 4000)
		} else {
			vars.user.preferences.hashtags.push({ name: hashtagName })
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has added the hashtag ${hashtagName}`, page || 'Profile', vars.user)
			alert('Hashtag added', 'success', 4000)
		}
	},
	/**
	 * @param {String} hashtagName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	addHashtag: async (hashtagName, vars, alert, page) => {
		if (!vars.user.preferences.hashtags || vars.user.preferences.hashtags === true || vars.user.preferences.hashtags === false) // vestigial fix
			vars.user.preferences.hashtags = []
		const hashtagIndex = vars.user.preferences.hashtags.findIndex(hashtag => hashtag.name === hashtagName)
		if (hashtagIndex === -1) {
			vars.user.preferences.hashtags.push({ name: hashtagName })
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has added the hashtag ${hashtagName}`, page || 'Profile', vars.user)
			alert('Hashtag added', 'success', 4000)
		}
	},

	/**
 * @param {String} hashtagName
 * @param {Object} vars
 * @param {Function} alert
 * @param {String} page
 *
 * @return {Promise<void>}
 */
	removeHashtag: async (hashtagName, vars, alert, page) => {
		if (!vars.user.preferences.hashtags || vars.user.preferences.hashtags === true || vars.user.preferences.hashtags === false) // vestigial fix
			vars.user.preferences.hashtags = []
		const hashtagIndex = vars.user.preferences.hashtags.findIndex(hashtag => hashtag.name === hashtagName)
		if (hashtagIndex > -1) {
			vars.user.preferences.hashtags.splice(hashtagIndex, 1)
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has removed the hashtag ${hashtagName}`, page || 'Profile', vars.user)
			alert('Hashtag removed', 'success', 4000)
		}
	},

	/**
 * @param {String} hashtagName
 * @param {Object} vars
 * @param {Function} alert
 * @param {String} page
 *
 * @return {Promise<void>}
 */
	toggleHashtagStar: async (hashtagName, vars, alert, page) => {
		if (!vars.user.preferences.hashtags || vars.user.preferences.hashtags === true || vars.user.preferences.hashtags === false) // vestigial fix
			vars.user.preferences.hashtags = []
		const hashtagIndex = vars.user.preferences.hashtags.findIndex(hashtag => hashtag.name === hashtagName)
		if (hashtagIndex > -1) {
			vars.user.preferences.hashtags[hashtagIndex].starred = !vars.user.preferences.hashtags[hashtagIndex].starred
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(
				`User has ${vars.user.preferences.hashtags[hashtagIndex].starred ? 'starred' : 'unstarred'} the hashtag ${hashtagName}`,
				page || 'Profile',
				vars.user
			)
			alert('Hashtag starred', 'success', 4000)
		}
	},

	/**
	 * @author Jared
	 * @description Set of CTA Functions
	 *
	 */

	/**
	 * @param {String} ctaName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	toggleCTA: async(ctaName, vars, alert, page) => {
		if (!vars.user.preferences.ctas)
			vars.user.preferences.ctas = []
		const ctaIndex = vars.user.preferences.ctas.findIndex(cta => cta.name === ctaName)
		if (ctaIndex > -1) {
			vars.user.preferences.ctas.splice(ctaIndex, 1)
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has removed the CTA ${ctaName}`, page || 'Profile',  vars.user)
			alert('CTA removed', 'success', 4000)
		} else {
			vars.user.preferences.ctas.push({name: ctaName})
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has added the CTA ${ctaName}`, page || 'Profile',  vars.user)
			alert('CTA added', 'success', 4000)
		}
	},
	/**
	 * @param {String} ctaName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	addCTA: async(ctaName, vars, alert, page) => {
		if (!vars.user.preferences.ctas)
			vars.user.preferences.ctas = []
		const ctaIndex = vars.user.preferences.ctas.findIndex(cta => cta.name === ctaName)
		if (ctaIndex === -1) {
			vars.user.preferences.ctas.push({name: ctaName})
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has added the CTA ${ctaName}`, page || 'Profile',  vars.user)
			alert('CTA added', 'success', 4000)
		}
	},
	/**
	 * @param {String} ctaName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	removeCTA: async(ctaName, vars, alert, page) => {
		if (!vars.user.preferences.ctas)
			vars.user.preferences.ctas = []
		const ctaIndex = vars.user.preferences.ctas.findIndex(cta => cta.name === ctaName)
		if (ctaIndex > -1) {
			vars.user.preferences.ctas.splice(ctaIndex, 1)
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has removed the CTA ${ctaName}`, page || 'Profile',  vars.user)
			alert('CTA removed', 'success', 4000)
		}
	},
	/**
	 * @param {String} ctaName
	 * @param {Object} vars
	 * @param {Function} alert
	 * @param {String} page
	 *
	 * @return {Promise<void>}
	 */
	toggleCTAStar: async(ctaName, vars, alert, page) => {
		if (!vars.user.preferences.ctas)
			vars.user.preferences.ctas = []
		const ctaIndex = vars.user.preferences.ctas.findIndex(cta => cta.name === ctaName)
		if (ctaIndex > -1) {
			vars.user.preferences.ctas[ctaIndex].starred = !vars.user.preferences.ctas[ctaIndex].starred
			await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
			helperService.basicLog(`User has ${vars.user.preferences.ctas[ctaIndex].starred ? 'starred' : 'unstarred'} the CTA ${ctaName}`, page || 'Profile',  vars.user)
			alert('CTA starred', 'success', 4000)
		}
	},
	getGPTQuery: (location, vars) => {
		return vars.user.preferences.askGPTPrompts?.[location] || null
	},
	setGPTQuery: async (location, query, vars) => {
		if (!vars.user.preferences.askGPTPrompts)
			vars.user.preferences.askGPTPrompts = {}
		vars.user.preferences.askGPTPrompts[location] = query
		helperService.basicLog(`User has updated a GPT query to ${query}`, location,  vars.user)
		await helperService.updateProfile(vars.user, 'preferences', vars.user.preferences)
	},
	uploadImageFromURL: async (imageURL, bucket, name) => {
		try {
			const response = await fetch('/api/aggie/services/uploadFileToServer', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ imageURL, bucket, name })
			})

			if (!response.ok) {
				throw new Error(`Error: ${response.statusText}`)
			}

			const data = await response.json()
			console.log('Success:', data)
			return data.newImgURL
		} catch (error) {
			console.error('Error:', error)
		}
	},
	getTopicListFromDescription: async(vars, description, returnExamples) => new Promise(async resolve => {
		helperService.fetch('/api/aiThreadRunner', {
			user: vars.user._id,
			team: vars.user.team,
			query: description,
			force: false,
			image: null,
			checkBeforeCache: false,
			count: 1,
			limit: returnExamples ? 20 : 10,
			textModel: null,
			imageModel: null,
			assistant: 'asst_FLbGFcMfxwYqFD8RQuLRNOwk',
			image_url: null
		}, async localResult => {
			const list = [],
				examples = {}
			localResult.response
				.replace(/[.+?]/gi, '')
				.replace(/【.+?】/gi, '')
				.split('\n')
				.filter(topic => vars.user.preferences?.contentTags ? vars.user.preferences.contentTags.indexOf(topic.replace('- ', '').trim()) === -1 : true)
				.forEach(line => (
					list.push(
						(line.split('.').length > 1 ? line.split('.')[1] : line.split('.')[0])
							.replace('- ', '')
							.split('[')?.[0]?.trim()
					)
				))

			// get 12 examples of images for each topic
			const promises = []

			for (const topic of list) {
				promises.push(new Promise(async resolve => {
					const subIndustries = await helperService.awaitFetch('/api/runner', {
						action: 'fetch',
						type: 'Industry',
						query: {subcategory:  topic},
						fields: {name: 1}
					})

					if (!subIndustries || subIndustries.length === 0) // remove topic from list
						list.splice(list.indexOf(topic), 1)
					resolve()
				}))
			}
			await Promise.all(promises)

			resolve(list)
		})
	}),
	getColorForStatus: (status) => {
		switch (status) {
		case 'draft':
			return '#FFCE1A'
		case 'approved':
			return '#01DC68'
		case 'posted':
			return '#4231BB'
		default:
			return '#ccc'
		}
	},
	getTest: (vars, testName) => {
		const result = vars.user.local?.tests.find(test => test.name === testName)?.condition

		if (!result)
			return helperServiceVars.vars.tests.find(test => test.name === testName)?.conditions.find(condition => condition.default)?.name
		else
			return result
	},
	/**
	 *
	 * @param vars
	 * @param alert
	 * @param message
	 * @param context
   * @param {String} [description]
   * @param {Number} [score]
	 */
	feedback: (vars, alert, message, context, description, score) => {
		const FeedbackComponent = () => {
			const [feedback, setFeedback] = useState({
				context,
				createdBy: vars.user._id,
				createdDate: new Date(),
				voteNumerator: score || 0,
				voteDenominator: 3,
			})

			return (<div>
				<p>{message}</p>
				<div className="flex justify-center text-5xl py-3 relative">
					<i
						onClick={() => {
							feedback.voteNumerator = 3
							setFeedback({...feedback})
						}}
						className={`block fa fa-smile mr-3 ${feedback.voteNumerator === 3 || feedback.voteNumerator === 0 ? 'text-aggie-green' : ''} 
						cursor-pointer hover:translate-y-1 transition-all duration-500 shadow-2xl`} />
					<i
						onClick={() => {
							feedback.voteNumerator = 2
							setFeedback({...feedback})
						}}
						className={`block fa fa-meh mr-3 ${feedback.voteNumerator === 2 || feedback.voteNumerator === 0 ? 'text-aggie-gray' : ''} 
						cursor-pointer hover:translate-y-1 transition-all duration-500 shadow-2xl`} />
					<i
						onClick={() => {
							feedback.voteNumerator = 1
							setFeedback({...feedback})
						}}
						className={`block fa fa-frown mr-3 ${feedback.voteNumerator === 1 || feedback.voteNumerator === 0 ? 'text-red-400' : ''} 
						cursor-pointer hover:translate-y-1 transition-all duration-500 shadow-2xl`} />
				</div>
				<textarea
					className="rounded-lg p-2 w-full text-sm mt-4 text-black bg-white h-24"
					onKeyUp={event => {
						// if enter
						if (event.key === 'Enter') {
							event.preventDefault()
							event.stopPropagation()
							feedback.feedback = event.target.value
							setFeedback({...feedback})
							helperService.manage(feedback, 'Feedback')
							alert()
							alert('Thank you for your feedback!', 'success')
						}
					}}
					onBlur={event => {
						feedback.feedback = event.target.value
						setFeedback({...feedback})
					}} placeholder={description || 'Add any details here to help us better understand your rating'} />
				<button
					onClick={() => {
						helperService.manage(feedback, 'Feedback')
						alert()
						alert('Thank you for your feedback!', 'success')
					}}
					className={tg.smallPrimaryButton + 'w-full rounded-lg block'}>Submit</button>
			</div>)
		}

		alert((<FeedbackComponent />), null, 10000)
	},
	getSessionsFromLogs: logs => {
		const timeGap = 12 * 60 * 60 * 1000

		logs.sort((a, b) => new Date(a.date) - new Date(b.date))

		const sessions = []
		let currentSession = {
			start: null,
			end: null,
			duration: 0,
		}

		logs.filter(log => {
			return log.page !== ''
		}).forEach((log, index) => {
			const logTime = new Date(log.date).getTime()

			if (index === 0) {
				// Start the first session
				currentSession.start = new Date(log.date)
				currentSession.end = new Date(log.date)
				currentSession.duration = currentSession.end.getTime() - currentSession.start.getTime()
				if (logs.length === 1)
					sessions.push(currentSession)

			} else {
				const previousLogTime = new Date(currentSession.end).getTime()
				const timeDifference = logTime - previousLogTime

				// currentSession.end = new Date(log.date)
				// currentSession.duration = currentSession.end.getTime() - currentSession.start.getTime()

				if (timeDifference > timeGap) {
					sessions.push(currentSession)
					currentSession =  {
						start: new Date(log.date),
						end: new Date(log.date),
						duration: 0,
					}
				}
				else {
					currentSession.end = new Date(log.date)
					currentSession.duration = currentSession.end.getTime() - currentSession.start.getTime()
				}
				if (index === logs.length - 1) {
					sessions.push(currentSession)
				}
			}
		})

		return sessions
		// // Add the last session
		// if (currentSession.length > 0) {
		// 	sessions.push(currentSession)
		// }
	}
}

module.exports = {...helperService, ...helperServiceAuth, ...helperServiceTextNumber, ...helperServiceVars}
